// Third-party
import Dictionary from '@/types/dictionary';
import { ProposalTemplateOptions } from '../redux/modules/types/proposal-v2';
import Nullable from '@/types/nullable';
import Locale from '@/types/locale';
import User from '@/types/user';

// Storage keys
const STORAGE_KEYS = {
  PROPOSAL_MODULES: 'proposal-modules',
  PROPOSAL_OPTIONS: 'proposal-style-options',
  PROPOSAL_OPTIONS_V2: 'proposal-style-options-v2',
  PROPOSAL_MODULES_ORDER: 'proposal-modules-order',
  PROPOSAL_DEFAULT_TEMPLATE: 'proposal-default-template',
  PROPOSAL_BRANDING_INFO: 'proposal-branding-info',
  LOCALE: 'locale',
  CONTACT_PORTFOLIO_PERSISTENCE_SOURCE: 'contact-portfolio-persistence-source',
  CONTACT_PERSISTENCE_SOURCE: 'contact-persistence-source',
};

/**
 * Local Storage util methods
 * IMPORTED FROM 1.0 -- Converted to TypeScript, might need some cleanup
 */

const storage = window.localStorage || { setItem: () => {}, getItem: () => undefined };

/**
 * Catch all version of storage.setItem to prevent issues when cache is full
 * TODO -- CON-1975 - Refactor the module to use an IndexedDB wrapper
 */
const setItem = (key: string, value: string) => {
  try {
    storage.setItem(key, value);
  } catch (err) {
    window.console.error(err);
  }
};

const getLocale = () => storage.getItem(STORAGE_KEYS.LOCALE) as Nullable<Locale>;

const setLocale = (locale: Locale) => {
  setItem(STORAGE_KEYS.LOCALE, locale);
};

const setProposalModuleKeys = (moduleKeys: string[]) => {
  setItem(STORAGE_KEYS.PROPOSAL_MODULES, JSON.stringify(moduleKeys));
};

const getProposalModuleKeys = (): Nullable<string[]> => {
  const proposalModuleKeys = storage.getItem(STORAGE_KEYS.PROPOSAL_MODULES);
  return proposalModuleKeys && JSON.parse(proposalModuleKeys);
};

const clearProposalOptions = () => {
  storage.removeItem(STORAGE_KEYS.PROPOSAL_OPTIONS);
};

// Proposal options - TODO: remove when we persist in db
const clearProposalV2Options = () => {
  storage.removeItem(STORAGE_KEYS.PROPOSAL_OPTIONS_V2);
};

const setProposalV2Options = (templateKey: string, options: ProposalTemplateOptions) => {
  setItem(`${STORAGE_KEYS.PROPOSAL_OPTIONS_V2}-${templateKey}`, JSON.stringify(options));
};

const getProposalV2Options = (templateKey: string): Nullable<ProposalTemplateOptions> => {
  const proposalTemplateOptions = storage.getItem(`${STORAGE_KEYS.PROPOSAL_OPTIONS_V2}-${templateKey}`);
  return proposalTemplateOptions && JSON.parse(proposalTemplateOptions);
};

const setDefaultProposalTemplate = (template: string): void => {
  setItem(STORAGE_KEYS.PROPOSAL_DEFAULT_TEMPLATE, template);
};

const getDefaultProposalTemplate = (): Nullable<string> => storage.getItem(STORAGE_KEYS.PROPOSAL_DEFAULT_TEMPLATE);

const setProposalBrandingInfo = (info: Dictionary<string | object | undefined>) => {
  setItem(STORAGE_KEYS.PROPOSAL_BRANDING_INFO, JSON.stringify(info));
};

const getProposalBrandingInfo = (): Nullable<Dictionary<string | object | undefined>> => {
  const info = storage.getItem(STORAGE_KEYS.PROPOSAL_BRANDING_INFO);
  return info ? JSON.parse(info) : null;
};

const clearProposalBrandingInfo = () => {
  storage.removeItem(STORAGE_KEYS.PROPOSAL_BRANDING_INFO);
};

const getPersistenceSource = (user: User, key: string) => {
  if (user == null) {
    return user;
  }

  return storage.getItem(`${user.id}-${key}`) as Nullable<string>;
};

const setPersistenceSource = (user: User, key: string, value?: string) => {
  if (user != null) {
    if (!value) {
      storage.removeItem(`${user.id}-${key}`);
    } else {
      setItem(`${user.id}-${key}`, value);
    }
  }
};

export default {
  STORAGE_KEYS,
  setProposalModuleKeys,
  getProposalModuleKeys,
  clearProposalOptions,
  getLocale,
  setLocale,
  clearProposalV2Options,
  setProposalV2Options,
  getProposalV2Options,
  setDefaultProposalTemplate,
  getDefaultProposalTemplate,
  setProposalBrandingInfo,
  getProposalBrandingInfo,
  clearProposalBrandingInfo,
  setPersistenceSource,
  getPersistenceSource,
};
